import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './GuardianPayment.css';

/**
 * PaymentCallback - Handles GoCardless redirect after payment
 * GoCardless redirects here with billing_request_id, we verify and redirect to success/failed
 */
const PaymentCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);

  useEffect(() => {
    const billingRequestId = searchParams.get('billing_request_id');

    if (!billingRequestId) {
      setError('Missing payment reference');
      setStatus('error');
      setTimeout(() => navigate('/payment-failed'), 2000);
      return;
    }

    verifyPayment(billingRequestId);
  }, [searchParams]);

  const verifyPayment = async (billingRequestId) => {
    try {
      setStatus('verifying');

      // Call backend to verify and process the payment
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/guardian-pay/verify/${billingRequestId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();

      if (data.success) {
        setStatus('success');
        // Redirect to success page with the payment ID
        setTimeout(() => {
          navigate(`/payment-success/${billingRequestId}`);
        }, 1000);
      } else {
        setStatus('failed');
        setError(data.message || 'Payment verification failed');
        setTimeout(() => navigate('/payment-failed'), 2000);
      }
    } catch (err) {
      console.error('Payment verification error:', err);
      setStatus('failed');
      setError('Unable to verify payment. Please contact support.');
      setTimeout(() => navigate('/payment-failed'), 2000);
    }
  };

  return (
    <div className="guardian-payment">
      <section style={{ marginTop: '40px' }}>
        <div className="container">
          <div className="panel panel-default panel-standard" style={{ maxWidth: '500px', margin: '0 auto' }}>
            <div className="panel-body text-center" style={{ padding: '50px' }}>
              {status === 'processing' || status === 'verifying' ? (
                <>
                  <div className="spinner-border text-primary" role="status" style={{ width: '60px', height: '60px', marginBottom: '30px' }}>
                    <span className="sr-only">Loading...</span>
                  </div>
                  <h3 style={{ color: '#12274e', marginBottom: '15px' }}>
                    {status === 'processing' ? 'Processing Payment...' : 'Verifying Payment...'}
                  </h3>
                  <p style={{ color: '#666' }}>
                    Please wait while we confirm your payment.
                  </p>
                </>
              ) : status === 'success' ? (
                <>
                  <div
                    style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '50%',
                      backgroundColor: '#28a745',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 30px',
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </div>
                  <h3 style={{ color: '#28a745', marginBottom: '15px' }}>Payment Verified!</h3>
                  <p style={{ color: '#666' }}>Redirecting to confirmation page...</p>
                </>
              ) : (
                <>
                  <div
                    style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '50%',
                      backgroundColor: '#dc3545',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 30px',
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </div>
                  <h3 style={{ color: '#dc3545', marginBottom: '15px' }}>Verification Failed</h3>
                  <p style={{ color: '#666' }}>{error || 'Unable to verify payment'}</p>
                  <p style={{ color: '#999', fontSize: '14px' }}>Redirecting...</p>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PaymentCallback;
